import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import notificationConfig from '../../config/notificationConfig.json';
import { apiErrorText, apiRequest } from '../../utils';
import './MailCatalogue.css';

/* Everything the platform sends on this company's behalf.

   ★ The list is DECLARED, not derived. "Every message with an email channel" would put
   password resets and internal ops digests on a customer's screen, and would do it silently
   the next time somebody adds one — so the dispatcher registry tags the ten a customer may
   see and this reads only those.

   ★ The bell sentence comes from the app's own `notifications` bundle, keyed by message name,
   which is exactly how the real feed renders it. That is deliberate: a catalogue that
   described a notification in its own words would drift from the one that lands, and the
   drift would be invisible. Same reason the mail preview builds the real mail server-side
   rather than showing a template.

   ★ It also says what the customer can CHANGE — the sign-off, one tab away. A screen that
   only lists what we do on your behalf, with nothing to press, is a brochure. */

const bellIcon = (name) => (notificationConfig[name] || {}).icon || 'bell';

const MailCatalogue = ({ onGoToSignOff }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);          // which row is expanded
  const [preview, setPreview] = useState({});      // message_name -> rendered mail
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', '/company/comms/catalogue')
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const out = [];
    ((data && data.messages) || []).forEach((m) => {
      const last = out[out.length - 1];
      if (last && last.group === m.group) last.messages.push(m);
      else out.push({ group: m.group, messages: [m] });
    });
    return out;
  }, [data]);

  /* The real mail, fetched once per message and kept. Nothing is sent — the server builds
     the same DefaultMail a send would and reads it instead of handing it to SendGrid. */
  const expand = (name) => {
    if (open === name) { setOpen(null); return; }
    setOpen(name);
    if (preview[name] || busy) return;
    setBusy(true);
    apiRequest('POST', `/company/comms/catalogue/${name}/preview`)
      .then((res) => setPreview((p) => ({ ...p, [name]: res })))
      .catch((err) => setPreview((p) => ({ ...p, [name]: { error: apiErrorText(t, err) } })))
      .finally(() => setBusy(false));
  };

  if (error) return <p className="bravos-cal__notice">{error}</p>;
  if (data === null) return <span className="auth__spinner" />;

  const sig = data.signature || {};
  const signedBy = sig.source === 'company' ? sig.name : sig.bu_name;

  return (
    <div className="mcat">
      <p className="bravos-q__hint">{t('catalogueNote')}</p>

      {/* What they control, said before the list rather than after it. */}
      <p className="ce-note mcat-signed">
        {t('catalogueSignedBy', { name: signedBy || t('signOffNeutral') })}{' '}
        <button type="button" className="gl-link" onClick={onGoToSignOff}>
          {t('catalogueChangeSignOff')}
        </button>
      </p>
      {!data.sample_event && <p className="ce-note">{t('catalogueNoEventYet')}</p>}

      {groups.map(({ group, messages }) => (
        <div key={group}>
          <p className="bravos-cal__label mt-3">{t(`catalogueGroup_${group}`)}</p>
          {messages.map((m) => (
            <div key={m.message_name} className="mcat-row">
              <button type="button" className="mcat-row__main"
                      onClick={() => expand(m.message_name)}>
                <Icon name={bellIcon(m.message_name)} />
                <span className="mcat-row__body">
                  <span className="mcat-row__title">{t(`msg_${m.message_name}_title`)}</span>
                  <span className="mcat-row__when">{t(`msg_${m.message_name}_when`)}</span>
                </span>
                <span className="mcat-chips">
                  {m.channels.includes('email') && (
                    <span className="ce-pill">{t('channelEmail')}</span>
                  )}
                  {m.channels.includes('in_app') && (
                    <span className="ce-pill">{t('channelNotification')}</span>
                  )}
                  <span className="ce-pill gl-why">{t(`trigger_${m.trigger}`)}</span>
                </span>
                <Icon name={open === m.message_name ? 'angle-up' : 'angle-down'} />
              </button>

              {open === m.message_name && (
                <div className="mcat-detail">
                  {/* The bell, rendered by the same call the feed makes. */}
                  {m.channels.includes('in_app') && (
                    <p className="mcat-bell">
                      <Icon name="bell" />{' '}
                      {t(`notifications:${m.message_name}`, { ...m.dynamic })}
                    </p>
                  )}
                  {!m.channels.includes('email') ? (
                    <p className="ce-note">{t('catalogueBellOnly')}</p>
                  ) : preview[m.message_name] === undefined ? (
                    <span className="auth__spinner" />
                  ) : preview[m.message_name].error ? (
                    <p className="ce-note">{preview[m.message_name].error}</p>
                  ) : preview[m.message_name].email ? (
                    <>
                      <p className="mcat-subject">
                        {preview[m.message_name].email.subject}
                      </p>
                      {/* Sandboxed, exactly as the send composer does it: this is mail HTML
                          and srcDoc without `sandbox` would run it on our own origin. */}
                      <iframe
                        title={m.message_name}
                        className="mcat-frame"
                        sandbox=""
                        srcDoc={`<!doctype html><meta charset="utf-8">
<style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
font-size:14px;line-height:1.5;color:#222;margin:0;padding:14px;background:#fff}
img{max-width:100%}</style>
${preview[m.message_name].email.body_start || ''}
${preview[m.message_name].email.body_end || ''}`}
                      />
                    </>
                  ) : (
                    <p className="ce-note">{t('cataloguePreviewFailed')}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default MailCatalogue;
